import { TAU } from '../core/math';
import { RNG, rr } from '../core/rng';
import { enemies } from '../entities/enemies';
import { SFX } from '../platform/audio';
import { disposeGadget, makeDrone } from '../render/gadgets';
import { areaDamage, spawnRing } from '../sim/fx';
import { spawnBullet } from '../sim/weapons';
import { P } from '../world/player';
import { scene } from '../render/scene';
import { strikes } from '../world/pools';
import type { Union } from './unions';
/* ============================================================
   融合 · 第一批（2026-08-03）
   ============================================================

   機制說明（融合＝兩把滿級武裝合成一把，而且**空出一個武器槽**）
   與整批的設計原則寫在 `data/unions.ts`，這裡只有定義。
   第二批在 `data/unions-b.ts` —— 拆檔的理由是單檔 300 行上限。 */

export var UNIONS_A: Union[] = [
  {
    /* 軌道光柱 + 跳彈迴路 → 光柵陣列。
       行為改變：光柱不再是各自獨立的落點，落地之後彼此以電弧相連成一張網。
       原本兩把都是「點」，合起來變成「面」。 */
    id: 'u-lattice', n: '光柵陣列', kind: '融合', axes: ['範圍', '傷害'],
    union: { a: 'w-orbital', b: 'w-chain' },
    turn: 3, turnD: '邊框 → 補上對角線（網心不再是安全區）',
    d: function (lv) {
      return '四道光柱同時落下，落點之間拉出電弧，網內持續灼燒'
        + (lv < 2 ? '' : '（冷卻縮短，落雷更密）')
        + (lv < 3 ? '' : '；**電弧補上對角線，網心不再是安全區**');
    },
    /* ⚠️ 2026-08-05 配平（`pnpm crafts` 量到 ×0.69 —— 融合之後比兩把來源加起來還弱）。
       **融合吃掉兩把滿級武裝，理應是全遊戲最強的一檔**，而它原本連來源都不如。
       兩輪配平：冷卻 5.2 → 2.7、光柱 96 → 172、電弧 26 → 48（第一輪推到 ×2.15，還差一點）。

       等級（2026-08-05，`CRAFT_MAX`）：
         Lv2  冷卻 2.7 → 2.05（吞吐 +32%），落點與電弧數量不變 —— 這把武裝的產能軸是施法頻率
         Lv3  電弧補上兩條對角線（行為改變，見 `fire` 尾段），光柱 172 → 306（`pnpm crafts`
              量出對角線本身貢獻很小 —— 四個落點多半分散在場邊，對角線常常掃不到東西，
              光靠「行為改變」不夠撐到 1.6–1.9× 帶寬，補數字才夠；172→260 量到 ×1.55、
              172→300 量到 ×1.60，卡在下緣，多留一點餘裕） */
    cd: function (lv) { return lv < 2 ? 2.7 : 2.05; },
    fire: function (lv) {
      /* 挑四個彼此分開的落點：擠在一起的話網會退化成一個點。 */
      var picks: any[] = [];
      for (var q = 0; q < 4; q++) {
        var best = null, bestC = -1;
        for (var i = 0; i < enemies.length; i++) {
          var e = enemies[i], tooClose = false;
          for (var p = 0; p < picks.length; p++) {
            if (Math.hypot(e.pos.x - picks[p].x, e.pos.z - picks[p].z) < 12) { tooClose = true; break; }
          }
          if (tooClose) continue;
          var c = 0;
          for (var j = 0; j < enemies.length; j++) {
            if (Math.hypot(enemies[j].pos.x - e.pos.x, enemies[j].pos.z - e.pos.z) < 8) c++;
          }
          if (c > bestC) { bestC = c; best = e; }
        }
        if (best) picks.push({ x: best.pos.x, z: best.pos.z });
        else picks.push({ x: P.pos.x + rr(-18, 18), z: P.pos.z + rr(-18, 18) });
      }
      for (var k = 0; k < picks.length; k++) {
        var st = strikes.get(); if (!st) break;
        st.m.position.set(picks[k].x, 0, picks[k].z);
        st.radius = 7.2; st.dmg = lv < 3 ? 172 : 306; st.who = 'u-lattice';
        st.life = 0; st.delay = 0.55 + k * 0.1; st.fired = false;
        st.ring.scale.setScalar(st.radius); st.ring.material.opacity = 0.8;
        st.col.scale.set(st.radius * 1.2, 60, st.radius * 1.2); st.col.material.opacity = 0;
        scene.add(st.m);
      }
      /* 電弧：相鄰落點之間沿線灑一串小範圍傷害。用既有的 areaDamage 串起來，
         不另外做一套線段碰撞 —— 讀起來是一張網，算起來還是點。 */
      for (var a = 0; a < picks.length; a++) {
        var b = picks[(a + 1) % picks.length];
        for (var t = 0.15; t < 1; t += 0.17) {
          var lx = picks[a].x + (b.x - picks[a].x) * t;
          var lz = picks[a].z + (b.z - picks[a].z) * t;
          areaDamage(lx, lz, 3.4, 48, 0x9fd8ff, null, 'u-lattice');
        }
      }
      /* Lv3 拿掉的第二條限制：**網只圍邊框，中心是安全區**。四道電弧沿著
         四邊形的外框連，對角線沒接 —— 敵人只要退到落點圍出的中間，
         這張「網」對它形同不存在。Lv3 之後補上兩條對角電弧（0↔2、1↔3），
         網心跟邊一樣燙。 */
      if (lv >= 3) {
        var diag = [[0, 2], [1, 3]];
        for (var di = 0; di < diag.length; di++) {
          var pa = picks[diag[di][0]], pb = picks[diag[di][1]];
          for (var td = 0.15; td < 1; td += 0.17) {
            var lxd = pa.x + (pb.x - pa.x) * td;
            var lzd = pa.z + (pb.z - pa.z) * td;
            areaDamage(lxd, lzd, 3.4, 48, 0x9fd8ff, null, 'u-lattice');
          }
        }
      }
      SFX.beam();
    }
  },
  {
    /* 感應無人機 + 衝擊斬波 → 僚機聯隊。
       行為改變：從「兩台點射」變成「四台環繞、齊射穿透斬波」——
       輸出從單體變成一整圈的清場。 */
    id: 'u-wing', n: '僚機聯隊', kind: '融合', axes: ['冷卻', '傷害'],
    union: { a: 'w-drone', b: 'w-pulse' },
    turn: 3, turnD: '從環上開火 → 從核心貫穿（近身死角消失）',
    d: function (lv) {
      return '四台僚機環繞，每 ' + (lv < 2 ? '0.4' : '0.3') + ' 秒朝外齊射穿透斬波'
        + (lv < 3 ? '' : '；**改從核心貫穿打出，近身死角消失**');
    },
    onEquip: function (o) {
      o.st = {
        d: [makeDrone(0x5fe3ff), makeDrone(0xffd24a), makeDrone(0x9fffe8), makeDrone(0xff8a3d)],
        a: 0, cd: 0
      };
    },
    onRemove: function (o) {
      if (o.st) for (var i = 0; i < o.st.d.length; i++) disposeGadget(o.st.d[i]);
      o.st = null;
    },
    update: function (lv, dt, o) {
      if (!o.st) return;
      var st = o.st, n = 4, R = 5.6;
      st.a += dt * 1.5;
      for (var i = 0; i < n; i++) {
        var ang = st.a + (i / n) * TAU;
        var g = st.d[i].g;
        g.position.set(P.pos.x + Math.cos(ang) * R, 2.6 + Math.sin(st.a * 2 + i) * 0.3, P.pos.z + Math.sin(ang) * R);
        g.rotation.y = -ang + Math.PI / 2;
        st.d[i].muzzle.visible = false;
      }
      st.cd -= dt;
      if (st.cd > 0) return;
      /* ⚠️ 2026-08-05 配平（量到 ×0.40，六個質變裡最差的）。
         兩輪配平：齊射間隔 1.1 → 0.4、單發 46 → 132（第一輪推到 ×1.29，仍是最弱的）。理由同 `u-lattice`：
         融合的代價是兩把滿級武裝 + 兩個槽位的投資，它不能比來源弱。

         等級（2026-08-05，`CRAFT_MAX`）：
           Lv2  齊射間隔 0.4 → 0.3（吞吐 +33%），四台僚機不變
           Lv3  其中一台改從核心貫穿打出（見下）。⚠️ `pnpm crafts` 量到：四台全部
                改成核心貫穿會把「近身死角」整段填滿，量到 ×3~4（壓垮曲線）——
                盲區只需要補一個角，不用四個方向都補；那一台的單發也砍到 75
                （其餘三台維持 132），量到 ×2.06 仍偏高，這樣壓到帶寬內。 */
      st.cd = lv < 2 ? 0.4 : 0.3;
      /* 齊射：四台各朝自己的外側射一道穿透斬波。
         鎖不鎖定都射 —— 它的價值是「一圈的清場」，不是點殺。 */
      var crit = RNG.next() < P.s.crit;
      for (var k = 0; k < n; k++) {
        var a2 = st.a + (k / n) * TAU;
        var ox = P.pos.x + Math.cos(a2) * R, oz = P.pos.z + Math.sin(a2) * R;
        /* Lv3 拿掉的第二條限制：**貼身死角**。原本斬波從僚機所在的環（R=5.6）
           才開始往外飛，玩家與環之間那圈空白它完全打不到 —— 貼上來的敵人
           反而是這把清場武裝的盲區。Lv3 之後**其中一台（k===0）改從核心貫穿
           打出**，近身也照樣挨打；其餘三台維持原本的外掃，盲區補一角就夠，
           不需要四台都改（見上方配平註解）。 */
        var center = lv >= 3 && k === 0;
        var sx = center ? P.pos.x : ox, sz = center ? P.pos.z : oz;
        spawnBullet(sx, sz, Math.atan2(Math.cos(a2), Math.sin(a2)),
          (center ? 75 : 132) * (crit ? P.s.critMul : 1), crit, 36, 99, 0, 2.0, 0.55, 'u-wing');
        st.d[k].muzzle.visible = true;
      }
      spawnRing(P.pos.x, P.pos.z, 0x5fe3ff, R - 1, R + 3, 0.22);
      SFX.slash();
    }
  }
];
